/**
 * Send Content Gift UseCase
 *
 * Validates inputs and delegates to GiftService to build payload and call CF.
 * On success, mirrors the server-side coins increment onto the content item so
 * its gift/tip count updates immediately (matching the like/comment-count
 * behavior), then refreshes the local wallet so the coins debit reflects in
 * Redux.
 *
 * Flutter source: send_gift_bottom_sheet.dart:_sendGift
 */
import type { SendGiftInput } from '@/domain/input/gift/sendGiftInput';
import type { GiftService } from '@/domain/services/gift/giftService';
import { RefreshWalletUseCase } from '@/domain/usecases/wallet/refreshWalletUseCase';
import { eventBus, APP_EVENT_KEYS } from '@/infrastructure/eventBus';

export class SendContentGiftUseCase {
  constructor(
    private readonly giftService: GiftService,
    private readonly refreshWalletUseCase: RefreshWalletUseCase = new RefreshWalletUseCase(),
  ) {}

  async execute(input: SendGiftInput): Promise<boolean> {
    if (input.instances <= 0) return false;

    const success = await this.giftService.executeSendGift(input);

    if (success) {
      // Mirror the coins the server adds to the media doc onto the in-memory
      // content item so the gift/tip count refreshes without a re-fetch. The
      // server-side increment equals giftInfo.coins = gift.coins * instances
      // (send_gift_bottom_sheet.dart:193).
      const updatedCoins = (input.media.coins ?? 0) + input.gift.coins * input.instances;
      eventBus.emit(APP_EVENT_KEYS.CONTENT_ITEM_UPDATED, {
        mediaId: input.media.id,
        patch: { coins: updatedCoins },
      });

      // TODO: @claude add a follow up task for this: processFeedTagsWatch on content-gift success — Flutter send_gift_bottom_sheet.dart:210-214

      this.refreshWalletUseCase.execute();
    }

    return success;
  }
}
